'use client';

// Legacy: pro-frontend/src/app/manage-portfolio/newpreview-portfolio/templates/
//   classic-template/classic-template.component.{ts,html,scss}
// Same left-sidebar shell as modern (wider `col-md-3` sidebar), but the main
// content only ever renders `display_title` + attachments + description —
// no education fields, no ResumeEntryLink cards (legacy classic never had them).

import { useMemo, useState } from 'react';

import './classic.css';

import type { PortfolioCategoryEntry } from '../../types';
import { isImageFileType } from './entry-helpers';
import { nl2brSanitize } from './sanitize-html';
import { SocialLinksNav } from './social-links';
import type { PortfolioTemplateProps } from './types';
import { useSectionScrollSpy } from './use-section-scrollspy';

function entryTitle(entry: PortfolioCategoryEntry): string {
  return String(entry.display_title || entry.title || 'Untitled');
}

export function ClassicTemplate({ data, isPublicView, theme, primaryColor, editHref }: PortfolioTemplateProps) {
  const [selectedPreviewImage, setSelectedPreviewImage] = useState<string | null>(null);

  const summary = data.summary || data.shortBio;
  const summaryTitle = data.summaryTitle || 'Profile';
  const showSummary = (data.summaryStatus === 1 || !data.summaryStatus) && Boolean(summary);
  const showHighlights =
    (data.highlightsStatus === 1 || !data.highlightsStatus) && data.hightlights && data.hightlights.length > 0;
  const showDescriptions = data.descriptionStatus === 1 || !data.descriptionStatus;
  const showAttachments = data.attachmentStatus === 1 || !data.attachmentStatus;

  const categories = (data.categories || []).filter((cat) => cat.title && cat.entries && cat.entries.length > 0);
  const designation = data.designation;

  const sectionIds = useMemo(() => {
    const ids: string[] = [];
    if (showSummary) ids.push('summary');
    if (showHighlights) ids.push('highlights');
    for (const cat of categories) {
      if (cat.id != null) ids.push(String(cat.id));
    }
    return ids;
  }, [categories, showHighlights, showSummary]);

  const activeSectionId = useSectionScrollSpy(sectionIds);

  function renderAttachments(entry: PortfolioCategoryEntry) {
    const files = entry.ResumeEntryFile || [];
    if (!showAttachments || files.length === 0) {
      return null;
    }
    const firstImage = files.find((f, idx) => isImageFileType(f.file_type) && idx === 0);
    const restImages = files.filter((f, idx) => idx !== 0 && isImageFileType(f.file_type));
    return (
      <div className="expe_attachmentDetails">
        {firstImage ? (
          <div className="row">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={firstImage.file_url}
              alt=""
              className="main-preview-img"
              onClick={() => setSelectedPreviewImage(firstImage.file_url || '')}
            />
          </div>
        ) : null}

        {restImages.length > 0 ? (
          <div className="portfolio_attach_wrap clearfix">
            {restImages.map((file, idx) => (
              <div className="portfolio_incear remove_pading_porfolio clearfix" key={String(file.id ?? idx)}>
                <div className="files_in img_thum_in" onClick={() => setSelectedPreviewImage(file.file_url || '')}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={file.file_url} alt="" />
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`kw-tpl-root kw-tpl-classic porfolio_pulbic_view ${theme}${
        isPublicView ? ' is-public-view' : ''
      }`}
      style={{ ['--primary-color' as string]: primaryColor } as React.CSSProperties}
    >
      <aside className="kw-tpl-sidebar porfolio_in_new_section">
        <div className="public_view_menu_port">
          {data.profile_image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img alt="" src={data.profile_image} className="kw-portfolio-avatar" />
          ) : (
            <div className="kw-portfolio-avatar kw-portfolio-avatar-fallback" aria-hidden>
              {(data.name || '?').charAt(0)}
            </div>
          )}
          <div className="public_view_menu_profile_detail hidden-xs">
            <h3>{data.name}</h3>
            {designation ? <h4>{designation}</h4> : null}
            {data.company ? <h5>{data.company}</h5> : null}
            {!isPublicView && editHref ? (
              <a href={editHref} className="classic-edit-link" title="Edit Portfolio">
                <i className="fa-solid fa-pen" aria-hidden="true" /> Edit
              </a>
            ) : null}
          </div>
          <div className="public_view_menu_profile_in">
            <h3>{data.name}</h3>
            {(designation || data.company) ? (
              <h4>
                {designation}
                {data.company ? `-${data.company}` : ''}
              </h4>
            ) : null}
          </div>
        </div>

        <nav className="portfolio_menus_public_view portfolio_menus_public_view_scroll_wrap hidden-xs">
          <ul className="portfolio_menus_public_view_scroll_ul">
            {showSummary ? (
              <li>
                <a
                  href="#summary"
                  className={activeSectionId === 'summary' ? 'active' : undefined}
                >
                  {summaryTitle && summary ? summaryTitle : 'Profile'}
                </a>
              </li>
            ) : null}
            {showHighlights ? (
              <li>
                <a
                  href="#highlights"
                  className={activeSectionId === 'highlights' ? 'active' : undefined}
                >
                  Highlights
                </a>
              </li>
            ) : null}
            {categories.map((cat) => (
              <li key={String(cat.id ?? cat.title)}>
                <a
                  href={`#${cat.id}`}
                  className={activeSectionId === String(cat.id) ? 'active' : undefined}
                >
                  {cat.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="portfolio_public_menu">
          <SocialLinksNav data={data} />
        </div>
      </aside>

      <main className="kw-tpl-main porfolio_public_view_detail">
        <div className="kw-tpl-main-inner porfolio_in_detail_task_inner">
          {showSummary ? (
            <section id="summary" className="kw-portfolio-section">
              <h3 className="for_summary_title">{summaryTitle || 'Profile'}</h3>
              <div className="portfolio_in_detail_content" id="section2">
                {summary ? <p dangerouslySetInnerHTML={{ __html: nl2brSanitize(summary) }} /> : null}
              </div>
            </section>
          ) : null}

          {showHighlights ? (
            <section className="portfolio_in_detail_content_art kw-portfolio-section" id="highlights">
              <h3>Highlights</h3>
              {data.hightlights.map((item) => (
                <div className="experience_title" key={String(item.id ?? item.display_title)}>
                  <h4>{entryTitle(item)}</h4>
                  {renderAttachments(item)}
                  <div className="detailed_section_portfolio gap_between_attachments_details">
                    {showDescriptions && (item.display_description || item.description) ? (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: nl2brSanitize(item.display_description || item.description),
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </section>
          ) : null}

          {categories.map((cat) => (
            <section
              className="portfolio_in_detail_content_art kw-portfolio-section"
              id={String(cat.id)}
              key={String(cat.id ?? cat.title)}
            >
              <h3>{cat.title}</h3>
              {(cat.entries || []).map((entry) => (
                <div className="experience_title" key={String(entry.id ?? entryTitle(entry))}>
                  <h4>{entryTitle(entry)}</h4>
                  {renderAttachments(entry)}
                  <div className="detailed_section_portfolio gap_between_attachments_details">
                    {showDescriptions && (entry.display_description || entry.description) ? (
                      <p
                        className="gap_between_attachments_details"
                        dangerouslySetInnerHTML={{
                          __html: nl2brSanitize(entry.display_description || entry.description),
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>
      </main>

      {selectedPreviewImage ? (
        <div className="preview-overlay" onClick={() => setSelectedPreviewImage(null)}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={selectedPreviewImage} className="preview-img" alt="Enlarged view" />
            <div className="preview-close" onClick={() => setSelectedPreviewImage(null)}>
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
